import { Navigate, useRoutes } from 'react-router-dom';
import { GPanelContactsPagesConfig } from './shared/pages-config';
import { useGpanelContactsPermissions } from './shared/hooks/use-gpanel-contacts-permissions';
import ProtectedRoute from '../../../shared/protected-route';
import { useUserSettings } from '../../../utils/use-user-settings';
import NotFoundPage from '../../../shared/not-found-page';
import GpanelContactsPage from './components/gpanel-contacts-page';
import GpanelOrganizationDirectoryGridPage from './components/gpanel-organization-directory-grid-page.tsx';
import GPanelContactsLabelsLogsPage from './components/gpanel-contacts-labels/gpanel-contacts-labels-logs-grid-page.tsx';
import GpanelContactsTrashLabelsPage from './components/gpanel-contacts-trash/labels/gpanel-contacts-trash-labels-grid-page.tsx';
import GpanelContactsTrashContactsPage from './components/gpanel-contacts-trash/contacts/gpanel-contacts-trash-contacts-grid-page.tsx';
import GpanelContactsLabelContactsGridPage from './components/gpanel-label-contacts-grid-page.tsx';
import GpanelContactsDetails from './components/gpanel-contacts/gpanel-contacts-details';
import GpanelContactsAllSyncs from './components/gpanel-contacts-sync/contacts/gpanel-contacts-all-syncs';
import GpanelContactsNewSync from './components/gpanel-contacts-sync/gpanel-contacts-new-sync';
import GpanelContactsEditSync from './components/gpanel-contacts-sync/gpanel-contacts-edit-sync';
import { ComponentType } from 'react';
import GpanelContactsLabelSyncs from './components/gpanel-contacts-sync/label/gpanel-contacts-sync-label';
import GpanelContactsUsersStatusSync from './components/gpanel-contacts-sync/gpanel-contacts-users-status-sync.tsx';

const contactDetailsRoute = {
  path: `${GPanelContactsPagesConfig.ContactsDetails.link}/:contactId`,
  element: (
    <ProtectedRoute
      component={GpanelContactsDetails}
      pageConfigItem={GPanelContactsPagesConfig.ContactsDetails}
    />
  ),
};

const editSyncRoute = {
  path: `:syncId/${GPanelContactsPagesConfig.EditContactSync.link}`,
  element: (
    <ProtectedRoute
      component={GpanelContactsEditSync}
      pageConfigItem={GPanelContactsPagesConfig.EditContactSync}
    />
  ),
};

const getSyncRoutes = (syncPage: ComponentType) => ({
  path: GPanelContactsPagesConfig.ContactSyncs.link,
  children: [
    {
      index: true,
      element: (
        <ProtectedRoute
          component={syncPage}
          pageConfigItem={GPanelContactsPagesConfig.ContactSyncs}
        />
      ),
    },
    {
      path: `:syncId/${GPanelContactsPagesConfig.UserStatusContactSync.link}`,
      element: (
        <ProtectedRoute
          component={GpanelContactsUsersStatusSync}
          pageConfigItem={GPanelContactsPagesConfig.UserStatusContactSync}
        />
      ),
    },
  ],
});

export default function GpanelContactsRoutes() {
  const { activeLabs } = useUserSettings();
  const { PERMISSIONS } = useGpanelContactsPermissions();

  const routes = useRoutes([
    {
      path: [GPanelContactsPagesConfig.ContactSyncs.link].join('/'),
      children: [
        {
          path: GPanelContactsPagesConfig.NewContactSync.link,
          element: (
            <ProtectedRoute
              component={GpanelContactsNewSync}
              pageConfigItem={GPanelContactsPagesConfig.NewContactSync}
            />
          ),
        },
        editSyncRoute,
      ],
    },
    {
      path: GPanelContactsPagesConfig.ContactSyncs.link,
      children: [editSyncRoute],
    },
    {
      path: '/*',
      element: <GpanelContactsPage />,
      children: [
        {
          index: true,
          element: PERMISSIONS.canViewGPanelContacts ? (
            <Navigate
              replace
              to={GPanelContactsPagesConfig.OrganizationDirectory.link}
            />
          ) : null,
        },
        {
          path: GPanelContactsPagesConfig.OrganizationDirectory.link,
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute
                  component={GpanelOrganizationDirectoryGridPage}
                  pageConfigItem={
                    GPanelContactsPagesConfig.OrganizationDirectory
                  }
                />
              ),
            },
            contactDetailsRoute,
          ],
        },
        {
          path: `${GPanelContactsPagesConfig.OrganizationLabels.link}/:labelId`,
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute
                  component={GpanelContactsLabelContactsGridPage}
                  pageConfigItem={GPanelContactsPagesConfig.OrganizationLabels}
                />
              ),
            },
            contactDetailsRoute,
            getSyncRoutes(GpanelContactsLabelSyncs),
            getSyncRoutes(GpanelContactsUsersStatusSync),
            {
              path: `${GPanelContactsPagesConfig.LabelsLogs.link}`,
              element: (
                <ProtectedRoute
                  component={GPanelContactsLabelsLogsPage}
                  pageConfigItem={GPanelContactsPagesConfig.LabelsLogs}
                />
              ),
            },
          ],
        },
        getSyncRoutes(GpanelContactsAllSyncs),
        getSyncRoutes(GpanelContactsUsersStatusSync),
        {
          path: GPanelContactsPagesConfig.TrashLabels.link,
          element: (
            <ProtectedRoute
              component={GpanelContactsTrashLabelsPage}
              pageConfigItem={GPanelContactsPagesConfig.TrashLabels}
            />
          ),
        },
        {
          path: GPanelContactsPagesConfig.TrashContacts.link,
          element: (
            <ProtectedRoute
              component={GpanelContactsTrashContactsPage}
              pageConfigItem={GPanelContactsPagesConfig.TrashContacts}
            />
          ),
        },
      ],
    },
  ]);

  if (!activeLabs.includes('gpanelcontacts')) {
    return <NotFoundPage />;
  }

  return <>{routes}</>;
}
